import React from "react";
import OverviewLayout from "@/components/dashboard/overview-v2/OverviewLayout";
import AlertBannerV2 from "@/components/dashboard/overview-v2/AlertBannerV2";
import ProfileCompletionCardV2 from "@/components/dashboard/overview-v2/ProfileCompletionCardV2";
import ProfileStatsCardV2 from "@/components/dashboard/overview-v2/ProfileStatsCardV2";
import { useProfileContext } from '@/contexts/ProfileContext';
import { useUnifiedPortfolioQuery } from '@/hooks/portfolio/useUnifiedPortfolioQuery';
import AdvancedOverview from '@/components/dashboard/overview-v2/AdvancedOverview';

const OverviewPage: React.FC = () => {
  const { profile, isLoading } = useProfileContext();
  const { data: unifiedItems = [], isLoading: portfolioLoading } = useUnifiedPortfolioQuery({
    profileId: profile?.id,
    includeDrafts: true,
    enabled: !!profile?.id
  });

  let completionPercent = 0;
  let aboutMeComplete = false;
  let portfolioComplete = false;
  let experienceComplete = false;
  
  if (profile) {
    aboutMeComplete = Boolean(profile.bio && profile.bio.trim().length > 0);
    portfolioComplete = unifiedItems.length > 0;
    experienceComplete = Boolean(profile.experience_items && profile.experience_items.length > 0);
    const completionItems = [aboutMeComplete, portfolioComplete, experienceComplete];
    const completedItems = completionItems.filter(Boolean).length;
    completionPercent = Math.round((completedItems / completionItems.length) * 100);
  }

  // Show loading state while portfolio data is loading
  if (isLoading || !profile || portfolioLoading) {
    return (
      <OverviewLayout>
        <div className="space-y-6">
          <div className="w-full bg-white rounded-2xl border border-gray-200 p-8 animate-pulse">
            <div className="mb-6">
              <div className="font-bold text-xl text-black">Profile Completion</div>
              <div className="text-gray-500 text-base mt-1">Complete your profile to get discovered</div>
            </div>
            <div className="flex items-center mb-8">
              <div className="flex-1 mr-4">
                <div className="bg-white border border-gray-200 rounded-full w-full h-5 flex items-center">
                  <div className="w-full h-full p-1">
                    <div className="h-full bg-gray-200 rounded-full w-1/2" style={{ width: '50%' }}></div>
                  </div>
                </div>
              </div>
              <span className="text-gray-700 font-semibold text-base min-w-[40px]">--%</span>
            </div>
          </div>
        </div>
      </OverviewLayout>
    );
  }

  // Show advanced overview if 100% complete OR if user has portfolio items (no rightSidebar)
  if (completionPercent === 100 || portfolioComplete) {
    return (
      <OverviewLayout>
        <AdvancedOverview />
      </OverviewLayout>
    );
  }

  // Otherwise show basic overview with sidebar
  return (
    <OverviewLayout rightSidebar={<ProfileStatsCardV2 />}>
      <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
        <div className="font-bold text-xl text-black mb-6">Overview</div>
        <AlertBannerV2 completionPercent={completionPercent} />
        <ProfileCompletionCardV2 />
      </div>
    </OverviewLayout>
  );
};

export default OverviewPage; 